// src/utils/validation.js
export const validators = {
  required: (value, fieldName) =>
    (value === null || value === undefined || value.toString().trim() === "") ? `${fieldName} is required` : "",

  minLength: (value, fieldName, length) =>
    value && value.trim().length < length
      ? `${fieldName} must be at least ${length} characters`
      : "",

  maxLength: (value, fieldName, length) =>
    value && value.trim().length > length
      ? `${fieldName} must not exceed ${length} characters`
      : "",

  regex: (value, fieldName, regex, message) =>
    value && !regex.test(value) ? message || `${fieldName} is invalid` : "",

  stringOnly: (value, fieldName) =>
    value && /[^a-zA-Z\s]/.test(value)
      ? `${fieldName} must contain only letters`
      : "",

  noSpecialChars: (value, fieldName) =>
    value && /[!@#$%^&*()_+={}\[\]<>?`~\\|;:"']/.test(value)
      ? `${fieldName} must not contain special characters`
      : "",

  numeric: (value, fieldName) =>
    value && isNaN(value) ? `${fieldName} must be a number` : "",

  float: (value, fieldName) =>
    value && isNaN(parseFloat(value))
      ? `${fieldName} must be a float number`
      : "",

  email: (value, fieldName = "Email") => {
    if (!value) return "";
    const regex =
      /^[a-zA-Z0-9](\.?[a-zA-Z0-9_-])*@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
    return !regex.test(value) ? `${fieldName} format is invalid` : "";
  },

  password: (value, fieldName = "Password") => {
    if (!value) return ""; // optional if not required
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;
    return !regex.test(value)
      ? `${fieldName} must be at least 8 chars, include uppercase, lowercase, number & special character`
      : "";
  },

  minNumber: (value, fieldName, min) =>
    value && value < min ? `${fieldName} must be at least ${min}` : "",

  maxNumber: (value, fieldName, max) =>
    value && value > max ? `${fieldName} must be at most ${max}` : "",

  file: (value, fieldName, options = {}) => {
    if (!value) return options.optional ? "" : `${fieldName} is required`;

    if (!(value instanceof File)) return `${fieldName} must be a valid file`;

    if (options.accept && !options.accept.includes(value.type)) {
      return `Invalid ${fieldName} type. Allowed: ${options.accept.join(", ")}`;
    }

    if (options.maxSize && value.size > options.maxSize) {
      return `${fieldName} must be smaller than ${Math.round(
        options.maxSize / 1024,
      )} KB`;
    }

    return "";
  },

  fileType: (value, fieldName, allowedTypes = []) => {
    if (!value) return "";
    if (!(value instanceof File)) return `${fieldName} must be a valid file`;

    const fileExtension = value.name.split('.').pop().toLowerCase();
    const isValidType = allowedTypes.includes(fileExtension);

    return !isValidType
      ? `${fieldName} must be one of: ${allowedTypes.join(', ')}`
      : "";
  },

  maxFileSize: (value, fieldName, maxSizeMB = 5) => {
    if (!value) return "";
    if (!(value instanceof File)) return `${fieldName} must be a valid file`;

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const isValidSize = value.size <= maxSizeBytes;

    return !isValidSize
      ? `${fieldName} must be smaller than ${maxSizeMB}MB`
      : "";
  },

  url: (value, fieldName = "URL") => {
    if (!value) return "";
    const regex =
      /^(https?:\/\/)?([\w-]+)+([\w\-._~:/?#[\]@!$&'()*+,;=.]+)$/i;
    return !regex.test(value) ? `Enter a valid ${fieldName}` : "";
  },

  date: (value, fieldName = "Date") => {
    if (!value) return "";
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    return !regex.test(value) ? `Enter a valid ${fieldName} (YYYY-MM-DD)` : "";
  },

  monthYear: (value, fieldName = "Month Year") => {
    if (!value) return "";
    const regex = /^\d{4}-\d{2}$/;
    return !regex.test(value) ? `Enter a valid ${fieldName} (YYYY-MM)` : "";
  },

  time: (value, fieldName = "Time") => {
    if (!value) return "";
    const regex = /^\d{2}:\d{2}$/;
    return !regex.test(value) ? `Enter a valid ${fieldName} (HH:MM)` : "";
  },

  enum: (value, fieldName, allowedValues, options = {}) => {
    if (value === undefined || value === null || value === "") {
      return options.optional ? "" : `${fieldName} is required`;
    }
    const valStr = value.toString();
    const allowedStrs = allowedValues.map((v) => v.toString());
    return !allowedStrs.includes(valStr)
      ? `${fieldName} must be one of [${allowedValues.join(", ")}]`
      : "";
  },

  ssn: (value, fieldName = "SSN") => {
    if (!value) return "";

    // 1. Remove hyphens and check if digits only
    const digits = value.replace(/-/g, "");
    if (!/^\d+$/.test(digits)) return `Invalid ${fieldName} format (numbers and hyphens only)`;

    // 2. Ensure exactly 9 digits
    if (digits.length !== 9) return `${fieldName} must be exactly 9 digits`;

    // 3. Reject invalid first 3 digits (Area Number)
    // Cannot be 000, 666, or in 900-999 range
    const area = digits.substring(0, 3);
    if (area === "000" || area === "666" || (parseInt(area) >= 900 && parseInt(area) <= 999)) {
      return `Invalid ${fieldName} area number`;
    }

    // 4. Reject invalid middle 2 digits (Group Number)
    // Cannot be 00
    const group = digits.substring(3, 5);
    if (group === "00") return `Invalid ${fieldName} group number`;

    // 5. Reject invalid last 4 digits (Serial Number)
    // Cannot be 0000
    const serial = digits.substring(5, 9);
    if (serial === "0000") return `Invalid ${fieldName} serial number`;

    // 6. Reject common fake SSNs
    const fakeSSNs = ["123456789", "111111111", "000000000", "999999999", "123123123"];
    if (fakeSSNs.includes(digits)) return `Invalid ${fieldName} (detected common fake number)`;

    // 7. Validate format: Either "XXXXXXXXX" or "XXX-XX-XXXX"
    const formatRegex = /^\d{9}$|^\d{3}-\d{2}-\d{4}$/;
    if (!formatRegex.test(value)) return `Format must be XXXXXXXXX or XXX-XX-XXXX`;

    return "";
  },
};

/**
 * Validate whole form
 */
export const validateForm = (values, rules) => {
  const errors = {};

  Object.keys(rules).forEach((field) => {
    const value = values[field];
    const fieldRules = rules[field];

    for (let r of fieldRules) {
      let errorMsg = "";

      switch (r.rule) {
        case "optional":
          if (!value || value.toString().trim() === "")
            errorMsg = "OPTIONAL_EMPTY";
          break;
        case "required":
          errorMsg = validators.required(value, r.label);
          break;
        case "minLength":
          errorMsg = validators.minLength(value, r.label, r.value);
          break;
        case "maxLength":
          errorMsg = validators.maxLength(value, r.label, r.value);
          break;
        case "regex":
          errorMsg = validators.regex(value, r.label, r.value, r.message);
          break;
        case "stringOnly":
          errorMsg = validators.stringOnly(value, r.label);
          break;
        case "noSpecialChars":
          errorMsg = validators.noSpecialChars(value, r.label);
          break;
        case "numeric":
          errorMsg = validators.numeric(value, r.label);
          break;
        case "float":
          errorMsg = validators.float(value, r.label);
          break;
        case "email":
          errorMsg = validators.email(value, r.label);
          break;
        case "password":
          errorMsg = validators.password(value, r.label);
          break;
        case "minNumber":
          errorMsg = validators.minNumber(value, r.label, r.value);
          break;
        case "maxNumber":
          errorMsg = validators.maxNumber(value, r.label, r.value);
          break;
        case "file":
          errorMsg = validators.file(value, r.label, r);
          break;
        case "url":
          errorMsg = validators.url(value, r.label);
          break;
        case "date":
          errorMsg = validators.date(value, r.label);
          break;
        case "monthYear":
          errorMsg = validators.monthYear(value, r.label);
          break;
        case "time":
          errorMsg = validators.time(value, r.label);
          break;
        case "enum":
          let normalizedValue = value;
          if (
            r.values &&
            (r.values.includes(0) || r.values.includes("0")) &&
            (r.values.includes(1) || r.values.includes("1"))
          ) {
            normalizedValue =
              value === "0" || value === 0
                ? 0
                : value === "1" || value === 1
                  ? 1
                  : value;
          }
          errorMsg = validators.enum(normalizedValue, r.label, r.values, r);
          break;
        case "ssn":
          errorMsg = validators.ssn(value, r.label);
          break;
        default:
          break;
      }

      if (errorMsg === "OPTIONAL_EMPTY") break;
      if (errorMsg) {
        errors[field] = errorMsg;
        break; // stop on first error
      }
    }
  });

  return errors;
};

export const validateArrayForm = (values, rules) => {
  let errors = {};

  Object.keys(rules).forEach((key) => {
    //  ARRAY (education / experience)
    if (Array.isArray(values[key])) {
      const rowErrorsArray = values[key].map((row) => {
        let rowErrors = {};
        Object.keys(rules[key] || {}).forEach((field) => {
          // Only validate if the field has validation rules (is an array)
          const fieldRules = rules[key][field];
          if (Array.isArray(fieldRules)) {
            // Create a temporary rules object with just this field's rules
            const tempRules = { [field]: fieldRules };
            const error = validateField(field, row[field], tempRules);
            if (error) rowErrors[field] = error;
          }
        });
        return rowErrors;
      });

      // Only add to errors if at least one row has an error
      if (rowErrorsArray.some(rowErr => Object.keys(rowErr).length > 0)) {
        errors[key] = rowErrorsArray;
      }
    }

    // 🔹 NORMAL FIELD
    else {
      const error = validateField(key, values[key], rules);
      if (error) errors[key] = error;
    }
  });

  return errors;
};

/**
 * Validate a single field (for onChange / onBlur)
 */
export const validateField = (name, value, rules) => {
  const fieldRules = rules[name];
  if (!fieldRules) return "";

  // Ensure fieldRules is an array to prevent "fieldRules is not iterable" error
  if (!Array.isArray(fieldRules)) {
    return "";
  }

  for (let r of fieldRules) {
    let errorMsg = "";

    switch (r.rule) {
      case "optional":
        if (!value || value.toString().trim() === "") return "";
        break;
      case "required":
        errorMsg = validators.required(value, r.label);
        break;
      case "minLength":
        errorMsg = validators.minLength(value, r.label, r.value);
        break;
      case "maxLength":
        errorMsg = validators.maxLength(value, r.label, r.value);
        break;
      case "regex":
        errorMsg = validators.regex(value, r.label, r.value, r.message);
        break;
      case "stringOnly":
        errorMsg = validators.stringOnly(value, r.label);
        break;
      case "noSpecialChars":
        errorMsg = validators.noSpecialChars(value, r.label);
        break;
      case "numeric":
        errorMsg = validators.numeric(value, r.label);
        break;
      case "float":
        errorMsg = validators.float(value, r.label);
        break;
      case "email":
        errorMsg = validators.email(value, r.label);
        break;
      case "password":
        errorMsg = validators.password(value, r.label);
        break;
      case "minNumber":
        errorMsg = validators.minNumber(value, r.label, r.value);
        break;
      case "maxNumber":
        errorMsg = validators.maxNumber(value, r.label, r.value);
        break;
      case "file":
        errorMsg = validators.file(value, r.label, r);
        break;
      case "fileType":
        errorMsg = validators.fileType(value, r.label, r.value);
        break;
      case "maxFileSize":
        errorMsg = validators.maxFileSize(value, r.label, r.value);
        break;
      case "url":
        errorMsg = validators.url(value, r.label);
        break;
      case "date":
        errorMsg = validators.date(value, r.label);
        break;
      case "time":
        errorMsg = validators.time(value, r.label);
        break;
      case "monthYear":
        errorMsg = validators.monthYear(value, r.label);
        break;
      case "enum":
        let fieldNormalizedValue = value;
        if (
          r.values &&
          (r.values.includes(0) || r.values.includes("0")) &&
          (r.values.includes(1) || r.values.includes("1"))
        ) {
          fieldNormalizedValue =
            value === "0" || value === 0
              ? 0
              : value === "1" || value === 1
                ? 1
                : value;
        }
        errorMsg = validators.enum(fieldNormalizedValue, r.label, r.values, r);
        break;
      case "ssn":
        errorMsg = validators.ssn(value, r.label);
        break;
      default:
        break;
    }

    if (errorMsg) return errorMsg;
  }

  return "";
};
